/**
 * Where a claim comes from, and where the chain breaks.
 *
 * An auditor does not ask what a number is. They ask where it came from, and
 * then where THAT came from, until they reach something a person saw or an
 * instrument recorded. This is that chain for one claim, with every link —
 * including the ones that do not exist.
 *
 * THREE KINDS OF FACT, kept apart because downstream they look identical and
 * downstream is where the damage is done:
 *
 *   observed   a person or instrument recorded it, with a timestamp
 *   derived    a model produced it; it has a key and a drift record
 *   asserted   a party said so, and nobody may have agreed they are right
 *
 * A fourth value, `missing`, is a link too. Returning only the steps that exist
 * makes a chain look short rather than broken, and hides the difference between
 * "nobody photographed this" and "this needed no photograph".
 *
 * NOTHING HERE IS SCORED. A chain of four observed links is not "92% reliable",
 * and inventing that number would be the least defensible thing on the page.
 */

export type LinkKind = "observed" | "derived" | "asserted" | "missing";

export interface Link {
  step: string;
  kind: LinkKind;
  what: string;
  source?: string;
  recordedBy?: string;
  recordedAt?: string;
  ageDays?: number;
  /** Why this link limits the claim, when it does. */
  concern?: string;
  /** The one thing that would firm this link up. */
  wouldStrengthen?: string;
}

export interface Chain {
  found: boolean;
  whyNot?: string;
  subject: { id: string; title: string; status?: string; where?: string };
  links: Link[];
  counts: Partial<Record<LinkKind, number>>;
  complete: boolean;
  weakest?: Link;
  gaps: Link[];
  reading: string;
  noScore?: string;
}

export const KIND_LABEL: Record<LinkKind, string> = {
  observed: "Observed",
  derived: "Derived from a model",
  asserted: "Asserted by a party",
  missing: "Nothing behind this",
};

/**
 * How an auditor reads a chain: strongest claim it can support.
 *
 * Taken from the weakest link and NOT from a tally. Three observed steps do
 * not outvote one missing one — that is precisely the arithmetic this module
 * exists to refuse.
 */
export function strengthOf(chain: Chain): LinkKind {
  const order: LinkKind[] = ["missing", "asserted", "derived", "observed"];
  for (const k of order) {
    if (chain.links.some((l) => l.kind === k)) return k;
  }
  return "observed";
}

/** Links whose age is worth a question, oldest first. */
export function aging(links: Link[], overDays = 365): Link[] {
  return links
    .filter((l) => (l.ageDays ?? 0) > overDays)
    .sort((a, b) => (b.ageDays ?? 0) - (a.ageDays ?? 0));
}
